// ============================================================================================================
// 📂 File: LanguageSelector.tsx
// 🧾 Purpose: Language selector component for extensions with voice guides integration
// 🧑‍💼 Author: Rodrigo Cuadra
// ============================================================================================================

import { useState, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useLanguageOptions } from '../../../hooks/useLanguageOptions';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';

interface VoiceGuide {
  id: number;
  uuid: string;
  name: string;
  description: string;
  language_code: string;
  dialect_code: string;
  voice_name: string;
  sound_path: string;
  is_fallback: boolean;
  enabled: boolean;
}

interface LanguageOption {
  value: string;
  label: string;
  voices: VoiceOption[];
}

interface VoiceOption {
  value: string;
  label: string;
  fullPath: string;
}

interface LanguageSelectorProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onVoiceChange?: (voice: string) => void;
  onSoundPrefixChange?: (fullPath: string) => void; // New callback for full path
  selectedVoice?: string;
  showVoiceSelector?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  showLabel?: boolean;
}

export function LanguageSelector({
  id,
  value,
  onChange,
  onVoiceChange,
  onSoundPrefixChange,
  selectedVoice = 'emma', // Default to 'emma' voice
  showVoiceSelector = true,
  disabled = false,
  error,
  className = '',
  showLabel = true
}: LanguageSelectorProps) {
  const { t } = useI18n();
  const { languageOptions, isLoading, errorMessage } = useLanguageOptions();

  // Auto-select first language if none selected
  useEffect(() => {
    if (!value && languageOptions.length > 0) {
      onChange(languageOptions[0].value);
      // Also set the first voice as selected
      if (onVoiceChange && languageOptions[0].voices.length > 0) {
        onVoiceChange(languageOptions[0].voices[0].value);
      }
    }
  }, [languageOptions, value, onChange, onVoiceChange]);

  // Get selected language option
  // Handle both "en-us" and "en-us-emma" formats
  const selectedLanguageOption = languageOptions.find(option => {
    if (value.includes('-') && value.split('-').length === 2) {
      // Value is just language code (e.g., "en-us")
      return option.value === value;
    } else if (value.includes('-') && value.split('-').length > 2) {
      // Value is language-voice (e.g., "en-us-emma")
      return value.startsWith(`${option.value}-`);
    }
    return false;
  });

  // Get available voices for selected language
  const availableVoices = selectedLanguageOption?.voices || [];

  // Get current combined value (language-voice) or just language
  const getCurrentValue = () => {
    if (!value) return '';

    // Check if value already has voice format (e.g., "en-us-emma")
    const parts = value.split('-');
    const hasVoice = parts.length >= 3;

    if (hasVoice) {
      // Value already has voice, return as-is
      return value;
    }

    // Value is just language (e.g., "en-us"), need to add voice
    if (showVoiceSelector && selectedVoice) {
      return `${value}-${selectedVoice}`;
    }

    // When showVoiceSelector is false, try to find first voice
    if (selectedLanguageOption && selectedLanguageOption.voices.length > 0) {
      return `${value}-${selectedLanguageOption.voices[0].value}`;
    }

    // Fallback: return value as-is
    return value;
  };

  // Helper function to get fullPath from language-voice combination
  const getFullPathFromValue = (langVoiceValue: string): string => {
    if (!langVoiceValue || !langVoiceValue.includes('-')) return '';

    const parts = langVoiceValue.split('-');
    if (parts.length >= 3) {
      // Format: "language-voice" (e.g., "en-us-callie")
      const language = parts.slice(0, -1).join('-'); // Get language part (e.g., "en-us")
      const voice = parts[parts.length - 1]; // Get voice part (e.g., "callie")

      const languageOption = languageOptions.find(option => option.value === language);
      if (languageOption) {
        const voiceOption = languageOption.voices.find(v => v.value === voice);
        if (voiceOption && voiceOption.fullPath) {
          return voiceOption.fullPath;
        }
      }
    }
    return '';
  };

  // Handle language change
  const handleLanguageChange = (newValue: string) => {
    if (newValue.includes('-') && newValue.split('-').length > 2) {
      // Format: "language-voice" (e.g., "en-us-callie")
      const parts = newValue.split('-');
      const voice = parts[parts.length - 1]; // Get voice part (e.g., "callie")

      // Always send combined value to onChange for proper saving
      onChange(newValue);

      // Update voice if callback provided
      if (onVoiceChange) {
        onVoiceChange(voice);
      }

      // Update sound prefix with full path
      if (onSoundPrefixChange) {
        const fullPath = getFullPathFromValue(newValue);
        if (fullPath) {
          onSoundPrefixChange(fullPath);
        }
      }
    } else {
      // Just language (legacy format or single voice)
      onChange(newValue);

      // Auto-select first voice if voice selector is shown
      if (showVoiceSelector && onVoiceChange) {
        const newLanguageOption = languageOptions.find(option => option.value === newValue);
        if (newLanguageOption && newLanguageOption.voices.length > 0) {
          const firstVoice = newLanguageOption.voices[0];
          onVoiceChange(firstVoice.value);

          // Update sound prefix with full path
          if (onSoundPrefixChange && firstVoice.fullPath) {
            onSoundPrefixChange(firstVoice.fullPath);
          }
        }
      }
    }
  };

  // Handle voice change
  const handleVoiceChange = (newVoice: string) => {
    if (onVoiceChange) {
      onVoiceChange(newVoice);
    }

    // Update sound prefix with full path when voice changes
    if (onSoundPrefixChange && value) {
      const currentLanguage = value.includes('-') && value.split('-').length > 2
        ? value.split('-').slice(0, -1).join('-')
        : value;
      const langVoiceValue = `${currentLanguage}-${newVoice}`;
      const fullPath = getFullPathFromValue(langVoiceValue);
      if (fullPath) {
        onSoundPrefixChange(fullPath);
      }
    }
  };

  return (
    <div className={`${showLabel ? 'space-y-4' : 'space-y-2'} ${className}`}>
      {/* Language Selector */}
      <div className="space-y-2">
        {showLabel && (
          <SimpleTooltip content={t('extensions.fields.languageCode.tooltip')}>
            <Label htmlFor="languageCode" className="text-left">
              {t('extensions.fields.languageCode.label')}
            </Label>
          </SimpleTooltip>
        )}

        {isLoading ? (
          <div className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm text-muted-foreground">
            Loading languages...
          </div>
        ) : (
          <select
            id={id || "languageCode"}
            value={getCurrentValue()}
            onChange={(e) => handleLanguageChange(e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring ${error ? 'border-red-500' : ''
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <option value="">{t('extensions.languageSelect.placeholder')}</option>
            {showVoiceSelector ? (
              // Show language-voice combinations
              languageOptions.map((option) =>
                option.voices.map((voice) => (
                  <option key={`${option.value}-${voice.value}`} value={`${option.value}-${voice.value}`}>
                    {option.label} - {voice.label}
                  </option>
                ))
              )
            ) : (
              // When showVoiceSelector is false, show language-voice combinations
              // but maintain compatibility with language-only values
              languageOptions.map((option) =>
                option.voices.map((voice) => (
                  <option key={`${option.value}-${voice.value}`} value={`${option.value}-${voice.value}`}>
                    {option.label} - {voice.label}
                  </option>
                ))
              ).flat()
            )}
          </select>
        )}

        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}

        {errorMessage && (
          <p className="text-sm text-yellow-600">{errorMessage}</p>
        )}
      </div>

      {/* Voice Selector - Only show when showVoiceSelector is true and showLabel is true (standalone mode) */}
      {showVoiceSelector && showLabel && selectedLanguageOption && (
        <div className="space-y-2">
          <SimpleTooltip content={t('extensions.fields.voice.tooltip')}>
            <Label htmlFor="voice" className="text-left">
              {t('extensions.fields.voice.label')}
            </Label>
          </SimpleTooltip>

          <select
            id="voice"
            value={selectedVoice}
            onChange={(e) => handleVoiceChange(e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring ${disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
          >
            {availableVoices.map((voice) => (
              <option key={voice.value} value={voice.value}>
                {voice.label}
              </option>
            ))}
          </select>

          {selectedLanguageOption && (
            <p className="text-xs text-muted-foreground">
              {t('extensions.fields.voice.info')}: {selectedLanguageOption.voices.length} {t('extensions.fields.voice.available')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
